/**
 * Unified Auth Context
 *
 * Provides a single interface for authentication in both modes:
 * - Regular mode: Uses Clerk (useRole + useApiClient)
 * - Embedded mode: Uses B24 session (useEmbeddedOrg + useEmbeddedApiClient)
 *
 * Dashboards and components use useAuth() without knowing which mode they're in.
 */
import { useContext, ReactNode } from 'react';
import {
  UnifiedAuthContext,
  type UnifiedAuthContextValue,
} from './unified-auth-context-object';

export type { UnifiedAuthContextValue };

/**
 * Hook for accessing unified auth context
 *
 * Works in both regular and embedded modes - just use this instead of
 * useRole()/useApiClient() or useEmbeddedOrg()/useEmbeddedApiClient()
 */
export function useAuth(): UnifiedAuthContextValue {
  const context = useContext(UnifiedAuthContext);
  if (!context) {
    throw new Error(
      'useAuth must be used within ClerkAuthProvider or EmbeddedAuthProvider'
    );
  }
  return context;
}

/**
 * Provider component - used internally by ClerkAuthProvider and EmbeddedAuthProvider
 */
export function UnifiedAuthProvider({
  children,
  value,
}: {
  children: ReactNode;
  value: UnifiedAuthContextValue;
}) {
  return (
    <UnifiedAuthContext.Provider value={value}>{children}</UnifiedAuthContext.Provider>
  );
}
